if (!customElements.get('liene-freeform-collage')) {
  customElements.define('liene-freeform-collage', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.mutations?.disconnect();
      this.imageObserver?.disconnect();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.mobile = matchMedia('(max-width: 749px)');
      this.tiles = [];
      this.imageObserver = new ResizeObserver(entries => {
        entries.forEach(entry => {
          const tile = entry.target.closest('[data-tile]');
          if (tile?.inView) this.resizeImages(tile);
        });
      });
      this.observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          entry.target.inView = entry.isIntersecting;
          this.update(entry.target);
        });
      }, { threshold: 0 });
      // The theme editor can replace children without reconnecting this element.
      // It can also connect the element before its templates have been inserted.
      this.mutations = new MutationObserver(() => this.syncTiles());
      this.mutations.observe(this, { childList: true, subtree: true });
      this.syncTiles();
      this.mobile.addEventListener('change', () => {
        this.tiles.forEach(tile => {
          this.clear(tile);
          const rect = tile.getBoundingClientRect();
          tile.inView = rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < innerHeight;
          this.update(tile);
        });
      }, options);
      window.addEventListener('resize', () => {
        this.tiles.filter(tile => tile.inView).forEach(tile => this.resizeImages(tile));
      }, options);
      this.watchPixelDensity();
      document.addEventListener('visibilitychange', () => this.tiles.forEach(tile => this.update(tile)), options);
    }
    syncTiles() {
      const next = [...this.querySelectorAll('[data-tile]')].filter(tile =>
        tile.querySelector('[data-media]') && tile.querySelector('[data-desktop]') && tile.querySelector('[data-mobile]')
      );
      this.tiles.filter(tile => !next.includes(tile)).forEach(tile => {
        this.observer.unobserve(tile);
        if (tile.mediaNode) this.imageObserver.unobserve(tile.mediaNode);
        this.clear(tile);
      });
      next.forEach(tile => {
        const desktop = tile.querySelector('[data-desktop]');
        const mobile = tile.querySelector('[data-mobile]');
        const media = tile.querySelector('[data-media]');
        if (this.tiles.includes(tile) && tile.desktopTemplate === desktop && tile.mobileTemplate === mobile && tile.mediaNode === media) return;
        if (tile.mediaNode) this.imageObserver.unobserve(tile.mediaNode);
        this.clear(tile);
        tile.desktopTemplate = desktop;
        tile.mobileTemplate = mobile;
        tile.mediaNode = media;
        const rect = tile.getBoundingClientRect();
        tile.inView = rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < innerHeight;
        this.observer.observe(tile);
        this.imageObserver.observe(media);
        this.update(tile);
      });
      this.tiles = next;
    }
    watchPixelDensity() {
      const density = matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
      density.addEventListener('change', () => {
        this.tiles.filter(tile => tile.inView).forEach(tile => this.resizeImages(tile));
        this.watchPixelDensity();
      }, { once: true, signal: this.abort.signal });
    }
    resizeImages(tile) {
      const media = tile.querySelector('[data-media]');
      media?.querySelectorAll('img[data-freeform-image]').forEach(image => this.sizeImage(image, tile, media));
    }
    sizeImage(image, tile, media) {
      const width = Number(image.dataset.originalWidth);
      const height = Number(image.dataset.originalHeight);
      const rect = media.getBoundingClientRect();
      if (!width || !height || !rect.width || !rect.height) return;
      const mode = this.mobile.matches ? 'm' : 'd';
      const fit = getComputedStyle(tile).getPropertyValue(`--${mode}fit`).trim();
      // Cover can enlarge an image beyond the card width when its height dominates.
      const heightDrivenWidth = rect.height * width / height;
      const displayedWidth = fit === 'cover' ? Math.max(rect.width, heightDrivenWidth)
        : fit === 'contain' ? Math.min(rect.width, heightDrivenWidth) : rect.width;
      // Let Shopify resize close to physical display pixels, rather than shrinking
      // an oversized source in the browser. Small buckets limit resize requests.
      const requestedWidth = Math.min(width, 5760,
        Math.ceil(displayedWidth * (window.devicePixelRatio || 1) / 8) * 8);
      if (!image.dataset.freeformSource) image.dataset.freeformSource = image.getAttribute('src');
      const url = new URL(image.dataset.freeformSource, document.baseURI);
      url.searchParams.set('width', requestedWidth);
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      if (image.getAttribute('src') !== url.href) image.setAttribute('src', url.href);
    }
    clear(tile) {
      const media = tile.querySelector('[data-media]');
      if (!media) return;
      media.querySelectorAll('video').forEach(video => {
        video.pause();
        video.removeAttribute('src');
        video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
        video.load();
      });
      media.replaceChildren();
    }
    update(tile) {
      const mode = this.mobile.matches ? 'mobile' : 'desktop';
      const media = tile.querySelector('[data-media]');
      const visible = tile.inView && tile.dataset[`${mode}Show`] !== 'false' && !document.hidden;
      if (!visible) {
        media.querySelectorAll('video').forEach(video => video.pause());
        return;
      }
      if (!media.childElementCount) {
        const content = tile.querySelector(`[data-${mode}]`).content.cloneNode(true);
        // Choose the source before insertion so a large fallback is not downloaded first.
        content.querySelectorAll('img[data-freeform-image]').forEach(image => this.sizeImage(image, tile, media));
        media.append(content);
        const video = media.querySelector('video');
        if (video) {
          video.muted = true;
          video.defaultMuted = true;
          video.controls = false;
          video.addEventListener('error', () => {
            if (!video.poster || !video.isConnected) return;
            const image = document.createElement('img');
            image.src = video.poster;
            image.alt = video.getAttribute('aria-label') || '';
            video.replaceWith(image);
          }, { once: true });
        }
      }
      this.resizeImages(tile);
      media.querySelectorAll('video').forEach(video => {
        video.muted = true;
        video.play().catch(() => { /* Keep the poster if the browser blocks autoplay. */ });
      });
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.mutations?.disconnect();
      this.imageObserver?.disconnect();
      this.tiles?.forEach(tile => this.clear(tile));
    }
  });
}
